import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';
import type {
  ClientStatusDetailsParams,
  ClientStatusDetailsResponse,
  MTFCountResponse,
  MTFPositionDetailsParams,
  MTFPositionDetailsResponse,
  MTFStatusDetailsParams,
  MTFStatusDetailsResponse,
} from '@/types/mtf';

// ================================================================
// API URL CONSTANTS
// ================================================================
const MTF_API_URL = import.meta.env.VITE_CONNECT_URL;

export const mtfApi = createApi({
  reducerPath: 'mtfApi',
  baseQuery: createBaseQuery(
    import.meta.env.VITE_API_BASE_URL || 'https://web.arhamshare.com/apb/v2',
  ),
  tagTypes: ['MTFCount', 'ClientStatusDetails', 'MTFStatusDetails', 'MTFPositionDetails'],
  endpoints: (builder) => ({
    getMTFCount: builder.query<MTFCountResponse, void>({
      query: () => ({
        url: '/clients/get-mtf-count',
        method: 'GET',
      }),
      providesTags: ['MTFCount'],
      transformResponse: (response: MTFCountResponse) => {
        // Ensure data structure consistency
        return {
          ...response,
          data: {
            totalCount: response.data?.totalCount || 0,
            mtfInterest: response.data?.mtfInterest || '0',
            mtfMaxAmount: response.data?.mtfMaxAmount || '0',
            mtfScripMaxAmount: response.data?.mtfScripMaxAmount || '0',
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('MTFCount', true),
    }),

    getClientStatusDetails: builder.query<ClientStatusDetailsResponse, ClientStatusDetailsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.status && params.status !== 'all') {
          queryParams.append('status', params.status);
        }

        if (params.searchTerm && params.searchTerm.trim()) {
          queryParams.append('searchTerm', params.searchTerm.trim());
        }

        if (params.pageNumber) {
          queryParams.append('pageNumber', params.pageNumber.toString());
        }

        if (params.pageSize) {
          queryParams.append('pageSize', params.pageSize.toString());
        }

        if (params.hardRefresh) {
          queryParams.append('hardRefresh', 'true');
        }

        queryParams.append('hardRefresh', 'true');

        return {
          url: `/clients/get-client-status-details?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['ClientStatusDetails'],
      transformResponse: (response: ClientStatusDetailsResponse) => {
        return {
          ...response,
          data: {
            totalCount: response.data?.totalCount || 0,
            pageNumber: response.data?.pageNumber || 1,
            pageSize: response.data?.pageSize || 10,
            data: response.data?.data || [],
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('MTFClientStatusDetails', false),
    }),

    getMTFPositionDetails: builder.query<MTFPositionDetailsResponse, MTFPositionDetailsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.searchTerm && params.searchTerm.trim()) {
          queryParams.append('searchTerm', params.searchTerm.trim());
        }

        if (params.pageNumber) {
          queryParams.append('pageNumber', params.pageNumber.toString());
        }

        if (params.pageSize) {
          queryParams.append('pageSize', params.pageSize.toString());
        }

        if (params.source) {
          queryParams.append('source', params.source);
        }

        return {
          url: `${MTF_API_URL}/v2/mtf/get-mtf-client-details?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['MTFPositionDetails'],
      transformResponse: (response: MTFPositionDetailsResponse) => {
        return {
          ...response,
          data: response.data || [],
          pageNumber: response.pageNumber || 1,
          pageSize: response.pageSize || 10,
          totalCount: response.totalCount || 0,
        };
      },
      transformErrorResponse: createApiErrorTransformer('MTFClientStatusDetails', false),
    }),

    downloadClientStatusDetails: builder.mutation<ArrayBuffer, ClientStatusDetailsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.status && params.status !== 'all') {
          queryParams.append('status', params.status);
        }

        if (params.fromDate) {
          queryParams.append('fromDate', params.fromDate);
        }

        if (params.toDate) {
          queryParams.append('toDate', params.toDate);
        }

        // Add download flag
        queryParams.append('isDownload', 'true');
        queryParams.append('hardRefresh', 'true');

        return {
          url: `/clients/get-client-status-details?${queryParams.toString()}`,
          method: 'GET',
          responseHandler: (response) => response.arrayBuffer(),
        };
      },
      transformErrorResponse: createApiErrorTransformer('ClientStatusDetailsDownload', false),
    }),

    getMTFStatusDetails: builder.query<MTFStatusDetailsResponse, MTFStatusDetailsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.status) {
          queryParams.append('status', params.status);
        }

        if (params.searchTerm && params.searchTerm.trim()) {
          queryParams.append('searchTerm', params.searchTerm.trim());
        }

        if (params.pageNumber) {
          queryParams.append('pageNumber', params.pageNumber.toString());
        }

        if (params.pageSize) {
          queryParams.append('pageSize', params.pageSize.toString());
        }

        queryParams.append('hardRefresh', 'true');

        return {
          url: `/clients/get-mtf-status-details?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['MTFStatusDetails'],
      transformResponse: (response: MTFStatusDetailsResponse) => {
        return {
          ...response,
          data: {
            totalCount: response.data?.totalCount || 0,
            pageNumber: response.data?.pageNumber || 1,
            pageSize: response.data?.pageSize || 10,
            data: response.data?.data || [],
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('MTFStatusDetails', false),
    }),
    mtfEsignProcess: builder.mutation<any, { clientId: string }>({
      query: ({ clientId }) => {
        return {
          url: `${MTF_API_URL}/v2/modification/mtf-esign-request`,
          method: 'GET',
          params: {
            clientCode: clientId,
            source: 'ap',
          },
        };
      },
      invalidatesTags: ['MTFStatusDetails', 'ClientStatusDetails'],
      transformResponse: (response: any) => {
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('MTFEsignProcess', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('MTFEsignProcess', 'MTF e-sign request initiated.', true);
        } catch {}
      },
    }),

    downloadMTFStatusDetails: builder.mutation<ArrayBuffer, MTFStatusDetailsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.status) {
          queryParams.append('status', params.status);
        }

        // Add download flag
        queryParams.append('isDownload', 'true');
        queryParams.append('hardRefresh', 'true');

        return {
          url: `/clients/get-mtf-status-details?${queryParams.toString()}`,
          method: 'GET',
          responseHandler: (response) => response.arrayBuffer(),
        };
      },
      transformErrorResponse: createApiErrorTransformer('MTFStatusDetailsDownload', false),
    }),
  }),
});

export const {
  useGetMTFCountQuery,
  useGetClientStatusDetailsQuery,
  useGetMTFStatusDetailsQuery,
  useGetMTFPositionDetailsQuery,
  useDownloadMTFStatusDetailsMutation,
  useDownloadClientStatusDetailsMutation,
  useMtfEsignProcessMutation,
} = mtfApi;
